import React, { useEffect, useState, useRef } from "react";
import "./Nav.scss";
import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { NavDropdown, Button } from "react-bootstrap";
import logoIUH from "../../image/Logo_IUH.png";
import { logoutUser } from "../../services/userServices";
import { toast } from "react-toastify";
import NavUser from "../Navbar/NavUser";
import { logout } from "../../redux/slice/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";

const NavHeader = () => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const userCode = user?.userData?.userCode || user?.userData?.userData?.userCode || user?.account?.userCode || "";

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!user?.isAuthenticated || !userCode) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Fetch initial notification list from Main Server
    fetch(`/api/notifications/${userCode}`, { headers: { Accept: 'application/vnd.bgx.v2+json' } })
      .then((res) => res.json())
      .then((data) => {
        const notificationsData = data?.data || data;
        if (Array.isArray(notificationsData)) {
          setNotifications(notificationsData);
          const unread = notificationsData.filter((n) => !n.isRead).length;
          setUnreadCount(unread);
        }
      })
      .catch((err) => console.log("Error fetching notifications:", err));

    const socketUrl = import.meta.env.VITE_SOCKET_URL || import.meta.env.VITE_MAIN_API_URL || 'http://localhost:1000';
    const socket = io(socketUrl, {
      transports: ["websocket"],
      autoConnect: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("register", userCode);
    });

    socket.on("new_notification", (data) => {
      setNotifications((prev) => {
        const isDuplicate = prev.some(
          (n) =>
            (n._id && data._id && n._id === data._id) ||
            (n.title === data.title && n.message === data.message)
        );
        if (isDuplicate) return prev;
        setUnreadCount((count) => count + 1);
        return [data, ...prev];
      });
    });

    return () => {
      if (socket) {
        socket.off("connect");
        socket.off("new_notification");
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [userCode]);

  const handleMarkAllRead = async () => {
    setUnreadCount(0);
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, isRead: true }))
    );

    if (!userCode) return;
    try {
      await fetch(`/api/notifications/mark-all-read/${userCode}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Accept: "application/vnd.bgx.v2+json" },
      });
    } catch (err) {
      console.log("Error marking notifications read:", err);
    }
  };

  const handleMarkOneRead = async (noti) => {
    if (noti.isRead) return;
    const targetId = noti._id || noti.id;

    setNotifications((prev) =>
      prev.map((n) => {
        const nId = n._id || n.id;
        if (targetId && nId && nId === targetId) {
          return { ...n, isRead: true };
        }
        if (n === noti) {
          return { ...n, isRead: true };
        }
        return n;
      })
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    if (targetId) {
      try {
        await fetch(`/api/notifications/${targetId}/read`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Accept: "application/vnd.bgx.v2+json" },
        });
      } catch (err) {
        console.log("Error marking single notification read:", err);
      }
    }
  };

  const handleLogout = async () => {
    let data = await logoutUser();

    dispatch(logout());
    if (data && data.ok) {
      toast.success("Đã đăng xuất");
      navigate("/login");
    } else {
      toast.error(data?.message || "Đã đăng xuất");
      navigate("/login");
    }
  };

  // Helper to get initial letter for avatar badge
  const getInitialLetter = () => {
    const name = user?.userData?.fullName || user?.userData?.name || user?.userData?.userCode || user?.account?.userCode || "";
    if (!name) return "U";
    const parts = name.trim().split(/\s+/);
    if (parts.length > 0) {
      const lastWord = parts[parts.length - 1];
      return lastWord.charAt(0).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="smartpark-header-wrapper">
      {/* Top Info Bar */}
      <div className="top-info-bar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-building text-primary"></i>
          <span>Trường Đại học Công nghiệp TP.HCM - Bãi Đỗ Xe Thông Minh IUH Parking</span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-1">
            <i className="bi bi-telephone-fill text-primary"></i>
            <span>0283.8940 390 - ext 838</span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <i className="bi bi-envelope-fill text-primary"></i>
            <span>csm@iuh.edu.vn</span>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="main-navbar-container">
        {/* Brand Logo */}
        <Link to="/" className="brand-logo-link">
          <img src={logoIUH} alt="Logo IUH" className="logo-iuh-img" />
          <div className="brand-text">
            <span>IUH Parking</span>
            <span className="brand-sub">ĐẠI HỌC CÔNG NGHIỆP TP.HCM</span>
          </div>
        </Link>

        {/* Header Links */}
        <div className="d-flex align-items-center gap-3">
          <div className="header-nav-menu">
            <NavLink to="/about" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
              <i className="bi bi-info-circle me-1"></i>
              Giới thiệu
            </NavLink>
          </div>

          {/* User Actions */}
          <div className="header-user-actions ms-2">
            {/* Notification Bell */}
            {user?.isAuthenticated && (
              <div className="position-relative">
                <button
                  type="button"
                  className="notification-bell-btn"
                  onClick={() => setShowNotifications((prev) => !prev)}
                  title="Thông báo"
                >
                  <i className="bi bi-bell fs-5"></i>
                  {unreadCount > 0 && (
                    <span className="badge-count">{unreadCount}</span>
                  )}
                </button>

                {/* Notification Popover */}
                {showNotifications && (
                  <div
                    className="position-absolute shadow-lg rounded-3 bg-white"
                    style={{
                      top: "120%",
                      right: 0,
                      width: "350px",
                      zIndex: 1050,
                      border: "1px solid #cbd5e1",
                      overflow: "hidden",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
                      <strong className="text-dark">
                        Thông báo {unreadCount > 0 && <span className="badge bg-danger rounded-pill ms-1">{unreadCount} mới</span>}
                      </strong>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none text-primary fw-bold"
                        onClick={handleMarkAllRead}
                      >
                        Đánh dấu tất cả đã đọc
                      </Button>
                    </div>
                    <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                      {notifications.length === 0 ? (
                        <p className="text-center text-muted my-4 small">Không có thông báo nào</p>
                      ) : (
                        notifications.map((noti, index) => {
                          const isUnread = !noti.isRead;
                          return (
                            <div
                              key={index}
                              onClick={() => handleMarkOneRead(noti)}
                              className="p-3 border-bottom transition-all cursor-pointer"
                              style={{
                                backgroundColor: isUnread ? "#f0f9ff" : "#ffffff",
                                borderLeft: isUnread ? "4px solid #2563eb" : "4px solid transparent",
                                cursor: isUnread ? "pointer" : "default",
                                opacity: isUnread ? 1 : 0.7,
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-start mb-1">
                                <strong className="text-dark small d-flex align-items-center gap-1">
                                  {noti.type === 'deposit' && <i className="bi bi-wallet2 text-success me-1"></i>}
                                  {noti.type === 'membership' && <i className="bi bi-shield-check text-primary me-1"></i>}
                                  {noti.type === 'warning' && <i className="bi bi-exclamation-triangle-fill text-danger me-1"></i>}
                                  {noti.type === 'reminder' && <i className="bi bi-clock-history text-warning me-1"></i>}
                                  {noti.type === 'transfer' && <i className="bi bi-arrow-left-right text-info me-1"></i>}
                                  {!['deposit', 'membership', 'warning', 'reminder', 'transfer'].includes(noti.type) && <i className="bi bi-bell text-primary me-1"></i>}
                                  {(noti.title || "Thông báo hệ thống").replace(/[🌟⏰🎉❌]/g, '').trim()}
                                </strong>
                                {isUnread && (
                                  <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill" style={{ fontSize: "0.65rem" }}>
                                    Mới
                                  </span>
                                )}
                              </div>
                              <p className="mb-1 text-secondary small">{noti.message || noti.content}</p>
                              <span className="text-muted d-block text-end" style={{ fontSize: "0.72rem" }}>
                                {new Date(noti.createdAt || noti.timestamp || Date.now()).toLocaleString("vi-VN")}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Auth Button or User Menu */}
            {user?.isAuthenticated ? (
              <NavDropdown
                title={
                  <div className="d-flex align-items-center gap-1">
                    {user?.userData?.avatar && !user.userData.avatar.includes("pravatar") ? (
                      <img
                        src={user.userData.avatar}
                        alt="Avatar"
                        className="rounded-circle border"
                        style={{ width: "36px", height: "36px", objectFit: "cover" }}
                      />
                    ) : (
                      <div className="user-avatar-badge">
                        <span>{getInitialLetter()}</span>
                      </div>
                    )}
                    <i className="bi bi-chevron-down text-muted small ms-1"></i>
                  </div>
                }
                id="user-profile-dropdown"
                align="end"
              >
                <NavDropdown.Item as={Link} to="/employee/info">
                  <i className="bi bi-person me-2 text-primary"></i>
                  Thông tin cá nhân
                </NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item onClick={handleLogout} className="text-danger">
                  <i className="bi bi-box-arrow-right me-2"></i>
                  Đăng xuất
                </NavDropdown.Item>
              </NavDropdown>
            ) : (
              <Link to="/login" className="btn-login-nav">
                Đăng nhập
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Sub Menu / NavUser */}
      {user?.isAuthenticated && (
        <NavUser whoIsLogging={user?.userData?.groupWithRoles?.name || user?.account?.groupWithRoles?.name || user?.userData?.userData?.Group?.name} />
      )}
    </div>
  );
};

export default NavHeader;
